/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or
 * distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly
 * prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SavedTableView } from 'types/entities/SavedTableView'
import { toNumericViewId } from 'utils/table/tableViewId'

export interface TableViewsState {
    views: SavedTableView[]
    hasLoaded: boolean
    activeViewIds: Record<string, number | null>
}

const initialState: TableViewsState = {
    views: [],
    hasLoaded: false,
    activeViewIds: {},
}

const normalizeSavedView = (view: SavedTableView): SavedTableView => {
    const id = toNumericViewId(view.id)
    const seller_id = toNumericViewId(view.seller_id)

    return {
        ...view,
        id: id ?? view.id,
        seller_id: seller_id ?? view.seller_id,
    }
}

const tableViewsSlice = createSlice({
    name: 'tableViews',
    initialState,
    reducers: {
        setTableViews(state, action: PayloadAction<SavedTableView[]>) {
            state.views = action.payload.map(normalizeSavedView)
            state.hasLoaded = true
        },
        pushTableView(state, action: PayloadAction<SavedTableView | undefined>) {
            if (!action.payload?.id) return
            const view = normalizeSavedView(action.payload)
            const index = state.views.findIndex((v) => v.id === view.id)
            if (index !== -1) {
                state.views[index] = view
            } else {
                state.views.unshift(view)
            }
        },
        removeTableView(state, action: PayloadAction<number>) {
            const viewId = toNumericViewId(action.payload)
            state.views = state.views.filter((v) => v.id !== viewId)
        },
        setActiveViewId(state, action: PayloadAction<{ tableId: string; viewId: number | null }>) {
            state.activeViewIds[action.payload.tableId] = toNumericViewId(action.payload.viewId)
        },
        resetState: () => initialState,
    },
})

export const { setTableViews, pushTableView, removeTableView, setActiveViewId, resetState } =
    tableViewsSlice.actions

export default tableViewsSlice.reducer
